import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Funnel, FilePdf, FileXls, Phone, MapPin, FileText, ShoppingCart, CircleNotch,
  Package, Paperclip, MagnifyingGlass, X, PencilSimple, Trash, Check,
} from '@phosphor-icons/react';

const PRODUCT_LABELS = {
  herrajes: 'Herrajes',
  pasamanos: 'Pasamanos',
  vidrio_templado: 'Vidrio templado',
  pvc: 'PVC',
  aluminio: 'Aluminio',
  techo: 'Techo',
  techo_curvo: 'Techo curvo',
  tablayeso: 'Tablayeso',
  puertas_automaticas: 'Puertas automáticas',
  reparacion: 'Reparación',
  peliculas: 'Películas',
  herraje_especial: 'Herraje especial',
  estructura_metalica: 'Estructura metálica',
  puertas_metal: 'Puertas metal',
  otros: 'Otros',
};

const CLIENT_TYPE_LABELS = {
  proyecto: 'Proyecto',
  vidrieria: 'Vidriería',
  instalador: 'Instalador',
  constructora: 'Constructora',
  cliente_final: 'Cliente final',
  otro: 'Otro',
};

const TYPE_META = {
  call: { label: 'Llamada', icon: Phone, color: '#1D4ED8' },
  visit: { label: 'Visita', icon: MapPin, color: '#F59E0B' },
  quote: { label: 'Cotización', icon: FileText, color: '#A855F7' },
  sale: { label: 'Venta', icon: ShoppingCart, color: '#10B981' },
};

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? iso.slice(0, 16).replace('T', ' ') : '—';


// Grouped list view for the activities table (collapsible by vendor/branch/type/customer)
const GroupedActivityList = ({ activities, groupBy, fmtQ }) => {
  const groups = activities.reduce((acc, a) => {
    let key;
    if (groupBy === 'vendor') key = a.vendor_name || '(Sin vendedor)';
    else if (groupBy === 'branch') key = a.branch || '(Sin sucursal)';
    else if (groupBy === 'type') key = ({ call: 'Llamadas', visit: 'Visitas', quote: 'Cotizaciones', sale: 'Ventas' })[a.type] || a.type;
    else if (groupBy === 'customer') key = a.customer_name || '(Sin cliente)';
    else key = 'Todo';
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});
  const sorted = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  return (
    <div className="divide-y divide-[var(--border)]">
      {sorted.map(([key, items]) => {
        const totalAmount = items.reduce((s, a) => s + (a.amount || 0), 0);
        return (
          <details key={key} className="px-4 py-2 group" data-testid={`grouped-${key.toLowerCase().replace(/\s/g,'-')}`}>
            <summary className="cursor-pointer flex items-center justify-between gap-2 hover:text-[#1D4ED8]">
              <span className="flex items-center gap-2">
                <span className="text-sm font-black text-[var(--text)]">{key}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--surface-muted)] text-[var(--text-muted)] font-bold">{items.length}</span>
              </span>
              {totalAmount > 0 && <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">{fmtQ(totalAmount)}</span>}
            </summary>
            <ul className="mt-2 space-y-1 pl-6 text-xs">
              {items.slice(0, 50).map(a => (
                <li key={a.id} className="flex justify-between gap-2 text-[var(--text-muted)]">
                  <span className="truncate">{(a.happened_at || '').slice(0, 10)} · {a.customer_name || '—'} · <em>{a.vendor_name}</em></span>
                  <span className="font-bold text-[var(--text)] whitespace-nowrap">{a.amount ? fmtQ(a.amount) : ''}</span>
                </li>
              ))}
              {items.length > 50 && <li className="text-[10px] italic text-[var(--text-faint)]">… y {items.length - 50} más</li>}
            </ul>
          </details>
        );
      })}
    </div>
  );
};


const ReporteActividades = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    user_id: searchParams.get('user_id') || '',
    branch: searchParams.get('branch') || '',
    type: searchParams.get('type') || '',
    product: '',
    client_type: '',
    departamento: '',
    from_date: '',
    to_date: '',
  });
  const [activities, setActivities] = useState([]);
  const [productsAnalysis, setProductsAnalysis] = useState({ products: [], total_quote_records: 0, grand_total_amount: 0 });
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ pdf: false, xls: false });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [groupBy, setGroupBy] = useState('none');

  const buildParams = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [actsResp, prodsResp] = await Promise.all([
        api.get(`/work-plan/activities/detailed?${buildParams.toString()}`),
        api.get(`/work-plan/reports/products-analysis?${buildParams.toString()}`),
      ]);
      setActivities(actsResp.data.activities || []);
      setProductsAnalysis(prodsResp.data);
      if (actsResp.data.departamentos) setDepartamentos(actsResp.data.departamentos);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando reporte');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const [vResp, bResp] = await Promise.all([
        api.get('/work-plan/dashboard/team?days=30'),
        api.get('/work-plan/branches'),
      ]);
      const uniqueVendors = (vResp.data?.per_vendor || []).map(v => ({ id: v.user_id, name: v.user_name })).filter(v => v.id && v.name);
      setVendors(uniqueVendors);
      setBranches(bResp.data?.branches || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchData(); }, [filters]);

  const exportFile = async (kind) => {
    setExporting(prev => ({ ...prev, [kind]: true }));
    try {
      const url = kind === 'pdf'
        ? `/work-plan/reports/detailed/export/pdf?${buildParams.toString()}`
        : `/work-plan/reports/detailed/export/excel?${buildParams.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `reporte-detallado-${Date.now()}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch {
      toast.error('Error exportando');
    } finally {
      setExporting(prev => ({ ...prev, [kind]: false }));
    }
  };

  const clearFilters = () => setFilters({ user_id: '', branch: '', type: '', product: '', client_type: '', departamento: '', from_date: '', to_date: '' });

  // Compute KPIs from current rows
  const kpis = useMemo(() => {
    const k = { calls: 0, visits: 0, quotes: 0, sales: 0, quotedAmount: 0, soldAmount: 0 };
    activities.forEach(a => {
      if (a.type === 'call') k.calls++;
      if (a.type === 'visit') k.visits++;
      if (a.type === 'quote') { k.quotes++; k.quotedAmount += a.amount || 0; }
      if (a.type === 'sale') { k.sales++; k.soldAmount += a.amount || 0; }
    });
    return k;
  }, [activities]);

  const maxProductTimes = Math.max(1, ...productsAnalysis.products.map(p => p.times_quoted));

  return (
    <div className="p-4 lg:p-6 space-y-5 min-h-screen bg-[var(--bg)]">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-[var(--text)] flex items-center gap-2">
            <ChartIcon /> Reporte Detallado de Actividades
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">Llamadas · Visitas · Cotizaciones · Ventas — con análisis de productos cotizados</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportFile('pdf')}
            disabled={exporting.pdf || loading}
            className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-detailed-pdf">
            {exporting.pdf ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />}
            PDF
          </button>
          <button
            onClick={() => exportFile('xls')}
            disabled={exporting.xls || loading}
            className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-detailed-xls">
            {exporting.xls ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} weight="bold" />}
            Excel
          </button>
        </div>
      </header>

      {/* Filters */}
      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1.5"><Funnel size={14} />Filtros</h3>
          <button onClick={clearFilters} className="text-[10px] uppercase tracking-wider font-bold text-[#DC2626] hover:underline">Limpiar</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SelectFilter label="Vendedor" value={filters.user_id} onChange={(v) => setFilters({ ...filters, user_id: v })} options={vendors.map(v => ({ value: v.id, label: v.name }))} placeholder="Todos" testid="filter-vendor" />
          <SelectFilter label="Sucursal" value={filters.branch} onChange={(v) => setFilters({ ...filters, branch: v })} options={branches.map(b => ({ value: b, label: b }))} placeholder="Todas" testid="filter-branch" />
          <SelectFilter label="Tipo" value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })} options={[{ value: 'call', label: 'Llamada' }, { value: 'visit', label: 'Visita' }, { value: 'quote', label: 'Cotización' }, { value: 'sale', label: 'Venta' }]} placeholder="Todos" testid="filter-type" />
          <SelectFilter label="Producto cotizado" value={filters.product} onChange={(v) => setFilters({ ...filters, product: v })} options={Object.entries(PRODUCT_LABELS).map(([k, v]) => ({ value: k, label: v }))} placeholder="Cualquiera" testid="filter-product" />
          <SelectFilter label="Tipo de cliente" value={filters.client_type} onChange={(v) => setFilters({ ...filters, client_type: v })} options={Object.entries(CLIENT_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))} placeholder="Cualquiera" testid="filter-client-type" />
          <SelectFilter label="Departamento" value={filters.departamento} onChange={(v) => setFilters({ ...filters, departamento: v })} options={departamentos.map(d => ({ value: d, label: d }))} placeholder="Todos" testid="filter-departamento" />
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Desde</label>
            <input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Hasta</label>
            <input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid="filter-to" />
          </div>
        </div>
      </section>

      {/* KPI Strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard label="Llamadas" value={kpis.calls} color="#1D4ED8" icon={Phone} />
        <KpiCard label="Visitas" value={kpis.visits} color="#F59E0B" icon={MapPin} />
        <KpiCard label="Cotizaciones" value={kpis.quotes} color="#A855F7" icon={FileText} />
        <KpiCard label="Ventas" value={kpis.sales} color="#10B981" icon={ShoppingCart} />
        <KpiCard label="Cotizado Q" value={fmtQ(kpis.quotedAmount)} color="#A855F7" icon={FileText} small />
        <KpiCard label="Vendido Q" value={fmtQ(kpis.soldAmount)} color="#10B981" icon={ShoppingCart} small />
      </section>

      {/* Products Analysis */}
      {productsAnalysis.products.length > 0 && (
        <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
          <header className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-[var(--text)] flex items-center gap-2"><Package size={16} weight="bold" />Análisis de Productos Cotizados</h3>
            <span className="text-[10px] text-[var(--text-muted)]">
              {productsAnalysis.total_quote_records} cotización(es) · Total {fmtQ(productsAnalysis.grand_total_amount)}
            </span>
          </header>
          <div className="space-y-1.5">
            {productsAnalysis.products.map((p) => (
              <div key={p.product} className="flex items-center gap-3" data-testid={`product-row-${p.product}`}>
                <div className="w-32 shrink-0 text-xs font-bold text-[var(--text)] truncate">{PRODUCT_LABELS[p.product] || p.product}</div>
                <div className="flex-1 bg-[var(--bg)] rounded-full h-5 overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#A855F7] to-[#1D4ED8] flex items-center px-2"
                       style={{ width: `${(p.times_quoted / maxProductTimes) * 100}%`, minWidth: '24px' }}>
                    <span className="text-[10px] font-black text-white">{p.times_quoted}x</span>
                  </div>
                </div>
                <div className="w-28 shrink-0 text-right text-[11px] font-bold text-[var(--text)]">{fmtQ(p.total_amount)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Activities Table */}
      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-hidden">
        <header className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-black text-[var(--text)]">{activities.length} actividad(es)</h3>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)] hidden sm:inline">Agrupar por</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="text-xs bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] rounded px-2 py-1" data-testid="group-by-select">
              <option value="none">Sin agrupar</option>
              <option value="vendor">Vendedor</option>
              <option value="branch">Sucursal</option>
              <option value="type">Tipo</option>
              <option value="customer">Cliente</option>
            </select>
            {loading && <CircleNotch size={16} className="animate-spin text-[var(--text-muted)]" />}
          </div>
        </header>
        {activities.length === 0 ? (
          <div className="p-10 text-center text-[var(--text-muted)] text-sm">
            {loading ? 'Cargando...' : 'Sin registros con los filtros actuales'}
          </div>
        ) : groupBy !== 'none' ? (
          <GroupedActivityList activities={activities} groupBy={groupBy} fmtQ={fmtQ} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Vendedor</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-left">Cliente / Empresa</th>
                  <th className="px-3 py-2 text-left">Detalle</th>
                  <th className="px-3 py-2 text-right">Monto Q</th>
                  <th className="px-3 py-2 text-center">Ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {activities.map(a => {
                  const meta = TYPE_META[a.type] || {};
                  const Icon = meta.icon || FileText;
                  return (
                    <tr key={a.id} className="hover:bg-[var(--bg)]" data-testid={`activity-row-${a.id}`}>
                      <td className="px-3 py-2 text-[var(--text)] whitespace-nowrap">{fmtDate(a.happened_at)}</td>
                      <td className="px-3 py-2 text-[var(--text)] font-bold">{a.vendor_name || '—'} <span className="text-[10px] text-[var(--text-muted)] font-normal">/ {a.branch || '—'}</span></td>
                      <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: `${meta.color}20`, border: `1px solid ${meta.color}40` }}><Icon size={10} weight="bold" />{meta.label}</span></td>
                      <td className="px-3 py-2 text-[var(--text)]">{a.customer_name || '—'}{a.customer_phone && <span className="block text-[10px] text-[var(--text-muted)]">{a.customer_phone}</span>}</td>
                      <td className="px-3 py-2 text-[var(--text-muted)] max-w-[260px] truncate">
                        {a.type === 'call' && (a.outcome ? `[${a.outcome}] ${a.notes || ''}` : a.notes)}
                        {a.type === 'visit' && (
                          <span>
                            {CLIENT_TYPE_LABELS[a.client_type] || '—'} · {a.visit_description}
                          </span>
                        )}
                        {a.type === 'quote' && (
                          <span>
                            #{a.quote_number} · {(a.products || []).map(p => PRODUCT_LABELS[p] || p).join(', ')}
                            {a.quote_file_url && <Paperclip size={11} className="inline ml-1 text-emerald-500" />}
                          </span>
                        )}
                        {a.type === 'sale' && `[${a.business_tag || '—'}] ${a.notes || ''}`}
                      </td>
                      <td className="px-3 py-2 text-right font-black text-[var(--text)] whitespace-nowrap">{a.amount ? fmtQ(a.amount) : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => setSelectedActivity(a)} className="text-[#1D4ED8] hover:underline text-[10px] font-bold uppercase" data-testid={`view-activity-${a.id}`}>
                          <MagnifyingGlass size={12} weight="bold" className="inline" /> Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Detail Modal */}
      {selectedActivity && <ActivityDetailModal activity={selectedActivity} onClose={() => setSelectedActivity(null)} onChanged={() => { setSelectedActivity(null); fetchData(); }} />}
    </div>
  );
};

const SelectFilter = ({ label, value, onChange, options, placeholder, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const KpiCard = ({ label, value, color, icon: Icon, small }) => (
  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] p-3" style={{ borderLeftColor: color, borderLeftWidth: 3 }}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={12} weight="bold" style={{ color }} />
      <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">{label}</span>
    </div>
    <p className={small ? "text-base font-black text-[var(--text)]" : "text-2xl font-black text-[var(--text)]"}>{value}</p>
  </div>
);

const ActivityDetailModal = ({ activity, onClose, onChanged }) => {
  const a = activity;
  const meta = TYPE_META[a.type] || {};
  const { user } = useAuth();
  const isAdmin = ['admin', 'supervisor'].includes((user?.role || '').toLowerCase());
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: a.customer_name || '',
    customer_phone: a.customer_phone || '',
    amount: a.amount ?? '',
    notes: a.notes || '',
    happened_at: (a.happened_at || '').slice(0, 10),
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        notes: form.notes,
      };
      if (form.amount !== '' && form.amount != null) payload.amount = parseFloat(form.amount);
      if (form.happened_at) payload.happened_at = `${form.happened_at}T12:00:00+00:00`;
      await api.patch(`/work-plan/activities/${a.id}`, payload);
      toast.success('Actividad actualizada (queda en bitácora)');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar esta ${meta.label?.toLowerCase() || 'actividad'} de ${a.vendor_name}? Quedará respaldo en la bitácora.`)) return;
    try {
      await api.delete(`/work-plan/activities/${a.id}`);
      toast.success('Actividad eliminada (respaldo en bitácora)');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-5 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-3 border-b border-[var(--border)] pb-3">
          <h3 className="text-base font-black text-[var(--text)] flex items-center gap-2">
            <span style={{ color: meta.color }}>{meta.label}</span>
            <span className="text-[10px] text-[var(--text-muted)] font-normal">{fmtDate(a.happened_at)}</span>
            {a.edited && <span className="text-[9px] text-amber-500 font-bold uppercase">editada</span>}
          </h3>
          <div className="flex items-center gap-1">
            {isAdmin && !editing && (
              <>
                <button data-testid="activity-edit-button" onClick={() => setEditing(true)} title="Editar (admin)"
                  className="p-1.5 rounded text-[var(--text-muted)] hover:text-[#1D4ED8] hover:bg-[var(--bg)]"><PencilSimple size={16} /></button>
                <button data-testid="activity-delete-button" onClick={remove} title="Eliminar (admin)"
                  className="p-1.5 rounded text-[var(--text-muted)] hover:text-red-600 hover:bg-[var(--bg)]"><Trash size={16} /></button>
              </>
            )}
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)] p-1.5"><X size={18} /></button>
          </div>
        </header>
        {editing ? (
          <div className="space-y-2.5 text-xs" data-testid="activity-edit-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Cliente / Empresa</span>
              <input data-testid="edit-customer-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Teléfono</span>
              <input data-testid="edit-phone-input" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Monto Q</span>
                <input data-testid="edit-amount-input" type="number" min="0" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Fecha</span>
                <input data-testid="edit-date-input" type="date" value={form.happened_at}
                  onChange={(e) => setForm({ ...form, happened_at: e.target.value })}
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Notas</span>
              <textarea data-testid="edit-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] resize-none" />
            </label>
            <div className="flex gap-2 pt-1">
              <button data-testid="edit-save-button" onClick={save} disabled={saving}
                className="flex-1 h-9 rounded-md bg-[#1D4ED8] hover:bg-blue-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
                {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Guardar cambios
              </button>
              <button onClick={() => setEditing(false)} className="h-9 px-3 rounded-md border border-[var(--border)] text-[var(--text-muted)] text-xs font-bold">Cancelar</button>
            </div>
          </div>
        ) : (
        <dl className="space-y-2 text-xs">
          <DetailRow label="Vendedor" value={`${a.vendor_name || '—'} / ${a.branch || '—'}`} />
          {a.business_tag && <DetailRow label="Empresa" value={a.business_tag} />}
          <DetailRow label="Cliente / Empresa" value={a.customer_name} />
          {a.contact_person && <DetailRow label="Persona de contacto" value={a.contact_person} />}
          {a.customer_phone && <DetailRow label="Teléfono" value={a.customer_phone} />}
          {a.address && <DetailRow label="Dirección" value={a.address} />}
          {a.client_type && <DetailRow label="Tipo de cliente" value={CLIENT_TYPE_LABELS[a.client_type] || a.client_type} />}
          {a.departamento && <DetailRow label="Departamento" value={a.departamento} />}
          {a.visit_description && <DetailRow label="Descripción visita" value={a.visit_description} />}
          {a.outcome && <DetailRow label="Resultado llamada" value={a.outcome} />}
          {a.quote_number && <DetailRow label="N° Cotización" value={a.quote_number} />}
          {a.products && a.products.length > 0 && <DetailRow label="Productos" value={a.products.map(p => PRODUCT_LABELS[p] || p).join(', ')} />}
          {a.amount != null && <DetailRow label="Monto Q" value={fmtQ(a.amount)} />}
          {a.notes && <DetailRow label="Notas" value={a.notes} />}
          {a.quote_file_url && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Adjunto</p>
              <a href={a.quote_file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1D4ED8] hover:underline font-bold text-xs">
                <Paperclip size={12} /> Ver cotización
              </a>
            </div>
          )}
          {(a.location_lat && a.location_lng) && (
            <DetailRow label="GPS" value={
              <a href={`https://maps.google.com/?q=${a.location_lat},${a.location_lng}`} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:underline">
                {a.location_lat.toFixed(5)}, {a.location_lng.toFixed(5)}
              </a>
            } />
          )}
        </dl>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">{label}</p>
    <p className="text-[var(--text)] font-bold">{value || '—'}</p>
  </div>
);

const ChartIcon = () => <Funnel size={20} weight="bold" className="text-[#1D4ED8] inline" />;

export default ReporteActividades;
